import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
  Radio, Plane, Battery, Gauge, Compass, MapPin, Satellite,
  Activity, RefreshCw, AlertCircle, CheckCircle2, Wifi, WifiOff,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { projectsAPI } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

// ============== UTIL ==============

const fmtNum = (v, d = 1, suffix = '') => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return Number(v).toFixed(d) + suffix;
};

const fmtBattery = (v) => {
  if (v === null || v === undefined) return '—';
  return `${v}%`;
};

const rtkLabel = (status) => {
  if (status === 50) return { text: 'RTK FIX', color: '#22c55e' };
  if (status === 16) return { text: 'RTK FLOAT', color: '#eab308' };
  if (status === 0 && status !== null) return { text: 'GPS', color: '#94a3b8' };
  return { text: '—', color: '#64748b' };
};

const since = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s atrás`;
  if (s < 3600) return `${Math.floor(s / 60)}m atrás`;
  return `${Math.floor(s / 3600)}h atrás`;
};

// Custom drone marker (DivIcon, rotated by heading)
const droneIcon = (heading = 0, color = '#06b6d4', isOnline = true) => L.divIcon({
  className: 'live-drone-icon',
  iconSize: [44, 44],
  iconAnchor: [22, 22],
  html: `
    <div style="position:relative;width:44px;height:44px;">
      <div style="position:absolute;top:50%;left:50%;width:60px;height:60px;
        transform:translate(-50%,-50%);border-radius:50%;
        background:${isOnline ? color : '#475569'}33;
        animation:livePulse 2s infinite;"></div>
      <div style="position:absolute;top:50%;left:50%;
        transform:translate(-50%,-50%) rotate(${heading || 0}deg);
        width:36px;height:36px;display:flex;align-items:center;justify-content:center;
        background:${isOnline ? color : '#475569'};border:2px solid white;
        border-radius:50%;box-shadow:0 4px 12px ${isOnline ? color : '#475569'}88;">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
          <path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z"/>
        </svg>
      </div>
    </div>
  `,
});

// Center map on first drone or follow selection
function MapAutoCenter({ position, follow }) {
  const map = useMap();
  useEffect(() => {
    if (position && follow) {
      map.setView(position, map.getZoom() < 14 ? 16 : map.getZoom(), { animate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position?.[0], position?.[1], follow]);
  return null;
}

// ============== MAIN ==============

export default function LiveTelemetryPage() {
  const { user, isSuperAdmin } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [drones, setDrones] = useState({}); // drone_key -> latest frame
  const [trails, setTrails] = useState({}); // drone_key -> [[lat,lng], ...]
  const [selectedKey, setSelectedKey] = useState(null);
  const [followSelected, setFollowSelected] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [lastEventAt, setLastEventAt] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeout = useRef(null);

  // Tick state to refresh "since X" labels every second
  const [, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(i);
  }, []);

  // Load projects
  useEffect(() => {
    let mounted = true;
    projectsAPI.list().then((res) => {
      if (!mounted) return;
      setProjects(res.data || []);
      if (!isSuperAdmin && (res.data || []).length > 0) {
        setSelectedProject(res.data[0].id);
      }
    }).catch(() => {});
    return () => { mounted = false; };
  }, [isSuperAdmin]);

  // WebSocket subscription
  useEffect(() => {
    const connect = () => {
      const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsBase = (API || window.location.origin).replace(/^https?:/, wsProto);
      const filter = selectedProject ? `?project_id=${encodeURIComponent(selectedProject)}` : '';
      const ws = new WebSocket(`${wsBase}/api/ws/telemetry/live${filter}`);
      wsRef.current = ws;

      ws.onopen = () => setWsConnected(true);
      ws.onclose = () => {
        setWsConnected(false);
        // Auto-reconnect after 3s
        if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          setLastEventAt(new Date().toISOString());
          if (msg.event === 'ready' && Array.isArray(msg.active)) {
            const initial = {};
            const trailsInit = {};
            msg.active.forEach((f) => {
              initial[f.drone_key] = f;
              trailsInit[f.drone_key] = [[f.latitude, f.longitude]];
            });
            setDrones(initial);
            setTrails(trailsInit);
            // Auto-select first drone
            if (!selectedKey && msg.active.length > 0) {
              setSelectedKey(msg.active[0].drone_key);
            }
          } else if (msg.event === 'telemetry:frame' && msg.data) {
            const f = msg.data;
            const key = `${f.project_id}::${f.drone_serial}`;
            f.drone_key = key;
            setDrones((prev) => ({ ...prev, [key]: f }));
            setTrails((prev) => {
              const arr = prev[key] || [];
              const last = arr[arr.length - 1];
              if (!last || last[0] !== f.latitude || last[1] !== f.longitude) {
                const next = [...arr, [f.latitude, f.longitude]];
                // Cap trail at 500 points
                if (next.length > 500) next.splice(0, next.length - 500);
                return { ...prev, [key]: next };
              }
              return prev;
            });
            // Auto-select first drone if none selected
            setSelectedKey((prev) => prev || key);
          } else if (msg.event === 'drone:offline' && msg.data?.drone_key) {
            // Just mark as offline visually; next frame removes if older than 15s
          }
        } catch { /* ignore non-JSON */ }
      };
    };

    connect();
    // Keepalive ping
    const ping = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send('ping');
    }, 25000);
    return () => {
      clearInterval(ping);
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        try { wsRef.current.close(); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  // Auto-prune drones that haven't reported in 30s (visual offline)
  useEffect(() => {
    const i = setInterval(() => {
      const now = Date.now();
      setDrones((prev) => {
        const next = {};
        Object.entries(prev).forEach(([k, v]) => {
          const t = v.server_timestamp ? new Date(v.server_timestamp).getTime() : 0;
          if (now - t < 60_000) next[k] = v; // keep up to 60s
        });
        return next;
      });
    }, 5000);
    return () => clearInterval(i);
  }, []);

  const droneList = useMemo(() => Object.values(drones), [drones]);
  const selected = selectedKey ? drones[selectedKey] : null;
  const selectedTrail = selectedKey ? (trails[selectedKey] || []) : [];

  const isOnline = (frame) => {
    if (!frame?.server_timestamp) return false;
    return Date.now() - new Date(frame.server_timestamp).getTime() < 15_000;
  };

  const mapCenter = selected
    ? [selected.latitude, selected.longitude]
    : (droneList[0] ? [droneList[0].latitude, droneList[0].longitude] : [-15.7942, -47.8825]); // Brasilia default

  // ============== RENDER ==============

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="live-telemetry-page">
      {/* Pulse animation for drone marker */}
      <style>{`
        @keyframes livePulse {
          0% { transform: translate(-50%,-50%) scale(0.6); opacity: 0.9; }
          100% { transform: translate(-50%,-50%) scale(1.6); opacity: 0; }
        }
      `}</style>

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
            <Radio className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
              TELEMETRIA AO VIVO
            </h1>
            <p className="text-xs text-slate-400 uppercase tracking-wider mt-0.5">
              DJI Mobile SDK 5 — Live Map Dashboard
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Project filter (super_admin sees all options + "ALL"; admin/supervisor sees only their projects) */}
          {(isSuperAdmin || projects.length > 1) && (
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500"
              data-testid="live-project-filter"
            >
              {isSuperAdmin && <option value="">TODOS OS PROJETOS</option>}
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          {/* Connection badge */}
          <div
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold tracking-wide
              ${wsConnected ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-red-500/20 text-red-400 border border-red-500/40'}
            `}
            data-testid="live-ws-badge"
          >
            {wsConnected ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            {wsConnected ? 'CONECTADO' : 'OFFLINE'}
            {wsConnected && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
          </div>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        {/* Sidebar: drone list */}
        <div className="space-y-3" data-testid="live-drone-list">
          <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-cyan-400 tracking-wider">DRONES ATIVOS</h2>
              <span className="text-xs text-slate-500">{droneList.length}</span>
            </div>
            {droneList.length === 0 ? (
              <div className="text-center py-8" data-testid="live-empty-state">
                <Plane className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                <p className="text-xs text-slate-500">Aguardando telemetria...</p>
                <p className="text-[10px] text-slate-600 mt-1">
                  Inicie o app DJI no controle para ver drones aqui
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto -mr-2 pr-2">
                {droneList.map((d) => {
                  const online = isOnline(d);
                  const rtk = rtkLabel(d.rtk_status);
                  const isSel = selectedKey === d.drone_key;
                  return (
                    <button
                      key={d.drone_key}
                      onClick={() => setSelectedKey(d.drone_key)}
                      className={`w-full text-left rounded-lg p-3 transition-all border
                        ${isSel
                          ? 'bg-cyan-500/10 border-cyan-500/60 shadow-md shadow-cyan-500/20'
                          : 'bg-slate-800/50 border-slate-800 hover:border-slate-700'}
                      `}
                      data-testid={`live-drone-card-${d.drone_serial}`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded ${online ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'}`}>
                          {online ? 'AO VIVO' : 'INATIVO'}
                        </span>
                        <span className="text-[10px] font-bold tracking-wider" style={{ color: rtk.color }}>
                          {rtk.text}
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-white truncate">
                        {d.drone_model || 'Drone'}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono truncate">
                        {d.drone_serial}
                      </div>
                      {d.pilot_name && (
                        <div className="text-[11px] text-slate-300 mt-1 truncate">
                          {d.pilot_name}
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-400">
                        <span className="flex items-center gap-1"><Battery className="w-3 h-3" />{fmtBattery(d.battery_pct)}</span>
                        <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{fmtNum(d.altitude_m, 0, 'm')}</span>
                        <span className="flex items-center gap-1"><Satellite className="w-3 h-3" />{d.satellites ?? '—'}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected drone full panel */}
          {selected && (
            <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-4 space-y-3" data-testid="live-drone-detail">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-cyan-400 tracking-wider">TELEMETRIA</h3>
                <button
                  onClick={() => setFollowSelected((v) => !v)}
                  className={`text-[10px] font-bold tracking-wider px-2 py-1 rounded ${followSelected ? 'bg-cyan-500 text-slate-950' : 'bg-slate-700 text-slate-300'}`}
                  data-testid="live-follow-toggle"
                >
                  {followSelected ? 'SEGUINDO' : 'LIVRE'}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Stat icon={MapPin} label="LAT" value={fmtNum(selected.latitude, 6)} />
                <Stat icon={MapPin} label="LNG" value={fmtNum(selected.longitude, 6)} />
                <Stat icon={Gauge} label="ALTITUDE" value={fmtNum(selected.altitude_m, 1, ' m')} />
                <Stat icon={Activity} label="VEL" value={fmtNum(selected.speed_ms, 1, ' m/s')} />
                <Stat icon={Compass} label="PROA" value={fmtNum(selected.heading_deg, 0, '°')} />
                <Stat icon={Battery} label="BATERIA" value={fmtBattery(selected.battery_pct)} />
                <Stat icon={Satellite} label="GPS SAT" value={selected.satellites ?? '—'} />
                <Stat icon={CheckCircle2} label="MODO" value={selected.flight_mode || '—'} small />
              </div>
              {(selected.h_acc_m !== null && selected.h_acc_m !== undefined) && (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <Stat label="H. ACC" value={fmtNum(selected.h_acc_m, 3, ' m')} />
                  <Stat label="V. ACC" value={fmtNum(selected.v_acc_m, 3, ' m')} />
                </div>
              )}
              {selected.home_distance_m !== null && selected.home_distance_m !== undefined && (
                <div className="text-[11px] text-slate-400 pt-2 border-t border-slate-800">
                  Distância de Home: <span className="text-cyan-400 font-mono">{fmtNum(selected.home_distance_m, 1, ' m')}</span>
                </div>
              )}
              <div className="text-[10px] text-slate-500 font-mono">
                Atualizado: {since(selected.server_timestamp)}
              </div>
            </div>
          )}
        </div>

        {/* Map */}
        <div
          className="relative bg-slate-900 border border-slate-800 rounded-xl overflow-hidden"
          style={{ minHeight: '70vh' }}
          data-testid="live-map-container"
        >
          {/* LIVE badge overlay */}
          <div className="absolute top-3 left-3 z-[400] flex items-center gap-1.5 bg-red-600/90 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs font-bold text-white shadow-lg">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
            LIVE
            {lastEventAt && (
              <span className="text-[10px] opacity-80 ml-1 font-mono">
                {since(lastEventAt)}
              </span>
            )}
          </div>

          <MapContainer
            center={mapCenter}
            zoom={selected ? 16 : 5}
            style={{ height: '100%', width: '100%', minHeight: '70vh', background: '#0f172a' }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />

            {/* Selected drone trail */}
            {selectedTrail.length > 1 && (
              <Polyline
                positions={selectedTrail}
                pathOptions={{ color: '#06b6d4', weight: 3, opacity: 0.85 }}
              />
            )}

            {/* All drones */}
            {droneList.map((d) => {
              const online = isOnline(d);
              const isSel = selectedKey === d.drone_key;
              return (
                <Marker
                  key={d.drone_key}
                  position={[d.latitude, d.longitude]}
                  icon={droneIcon(
                    d.heading_deg,
                    isSel ? '#06b6d4' : (online ? '#10b981' : '#64748b'),
                    online,
                  )}
                  eventHandlers={{
                    click: () => setSelectedKey(d.drone_key),
                  }}
                >
                  <Popup>
                    <div className="text-xs">
                      <strong>{d.drone_model || 'Drone'}</strong>
                      <div className="font-mono text-[10px] text-slate-500">{d.drone_serial}</div>
                      <div className="mt-1">Alt: {fmtNum(d.altitude_m, 1, 'm')}</div>
                      <div>Vel: {fmtNum(d.speed_ms, 1, ' m/s')}</div>
                      <div>Bat: {fmtBattery(d.battery_pct)}</div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Auto-center on selected drone */}
            {selected && (
              <MapAutoCenter
                position={[selected.latitude, selected.longitude]}
                follow={followSelected}
              />
            )}
          </MapContainer>

          {/* No data overlay */}
          {droneList.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[400]">
              <div className="bg-slate-900/90 backdrop-blur-md border border-slate-700 rounded-xl px-6 py-5 max-w-md text-center pointer-events-auto">
                <AlertCircle className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                <h3 className="text-sm font-bold text-white mb-1">SEM TRANSMISSÃO ATIVA</h3>
                <p className="text-xs text-slate-400 mb-3">
                  Nenhum drone está enviando telemetria neste momento.
                </p>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Para ativar: instale o app <strong className="text-cyan-400">Gestão de RPAS Telemetria</strong> no
                  controle DJI (RC Pro / RC 2 / Smart Controller Enterprise) e faça login com o token do projeto.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============== STAT COMPONENT ==============

function Stat({ icon: Icon, label, value, small }) {
  return (
    <div className="bg-slate-800/60 rounded-lg px-3 py-2 border border-slate-800">
      <div className="flex items-center gap-1 text-[9px] font-bold tracking-wider text-slate-500 uppercase mb-0.5">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className={`font-mono font-bold text-cyan-400 ${small ? 'text-[11px]' : 'text-sm'} truncate`}>
        {value}
      </div>
    </div>
  );
}
